type FeedbackText = {
  feedbackTextExample: string;
  feedbackTextWhatToDo: string;
};

type FeedbackTextWithVariants = {
  sales: FeedbackText;
  procurement: FeedbackText;
};

type FeedbackTexts = {
  governingLaw: FeedbackText;
  disputeResolution: FeedbackText;
  confidentiality: FeedbackText;
  paymentTerm: FeedbackTextWithVariants;
  exclusionOfConsequentialLoss: FeedbackTextWithVariants;
  forceMajeure: FeedbackTextWithVariants;
  indemnities: FeedbackTextWithVariants;
  limitationOfLiability: FeedbackTextWithVariants;
  liquidatedDamages: FeedbackTextWithVariants;
};

type possibleClauses = keyof FeedbackTexts;

export const getGenericFeedbackTexts = (
  t: (key: string, params: Record<string, string>) => string,
  clause: keyof Pick<FeedbackTexts, 'governingLaw' | 'disputeResolution' | 'confidentiality'>,
) => {
  const feedbackTexts: Pick<
    FeedbackTexts,
    'governingLaw' | 'disputeResolution' | 'confidentiality'
  > = {
    governingLaw: {
      feedbackTextExample: t(
        'This Agreement shall be governed by and construed in accordance with the laws of (Chosen Jurisdiction), without regard to its conflict of law principles. The United Nations Convention on Contracts for the International Sale of Goods (CISG) shall not apply.',
        {
          _comment:
            'Example text for governing law clause in a contract, specifying the jurisdiction and excluding the CISG.',
        },
      ),
      feedbackTextWhatToDo: t(
        'Governing law clauses specify which law will apply to interpret and enforce the contract, providing clarity and minimizing disputes by establishing a clear legal framework. Review it carefully to ensure it aligns with your business needs, including choosing a law that offers favorable protections and where you have legal representation for handling potential disputes.',
        {
          _comment:
            'Explanation of what governing law clauses do and why they are important for business contracts.',
        },
      ),
    },
    disputeResolution: {
      feedbackTextExample: t(
        'Any dispute, controversy, or claim arising out of or in connection with this Agreement, including its existence, validity, interpretation, performance, breach, or termination, shall be finally settled by arbitration in accordance with the Rules of Arbitration of (Arbitration Institution) by one or more arbitrators appointed in accordance with said Rules. The seat of arbitration shall be (City, Country). The language of the arbitration shall be (Language). The decision of the arbitrator(s) shall be final and binding on both parties.',
        {
          _comment:
            'Example text for arbitration clause, specifying the rules, seat, and language of arbitration.',
        },
      ),
      feedbackTextWhatToDo: t(
        'Arbitration is a private dispute resolution process where parties present their case to a neutral third party. It offers advantages such as speed and confidentiality but may also involve significant costs. Therefore, the clause should be carefully reviewed to specify the types of disputes it covers, cost-sharing arrangements, and any exceptions, ensuring it aligns with your business needs and legal preferences.',
        {
          _comment:
            'Explanation of arbitration clauses, their advantages, and considerations for reviewing them.',
        },
      ),
    },
    confidentiality: {
      feedbackTextExample: t(
        'Each Party agrees to keep all Confidential Information strictly confidential and not to disclose it to any third party, except as expressly permitted under this Agreement or with the prior written consent of the disclosing Party. Confidential Information shall only be used for the purpose of fulfilling the receiving Party’s obligations under this Agreement. The receiving Party shall take all reasonable measures to protect the confidentiality of such information and ensure that any permitted disclosures comply with the terms of this Agreement.',
        {
          _comment:
            'Example text for confidentiality clause, specifying obligations to protect sensitive information.',
        },
      ),
      feedbackTextWhatToDo: t(
        'A confidentiality clause requires that information be safeguarded and prevents its sharing or misuse beyond what has been authorized by the disclosing party.\n\nEnsure clarity on what types of information are protected, the responsibilities of each party, and the consequences of a breach to ensure it aligns with your business needs and legal preferences.',
        {
          _comment:
            'Explanation of confidentiality clauses, their purpose, and considerations for reviewing them.',
        },
      ),
    },
  };
  return feedbackTexts[clause] as FeedbackText;
};
export const getFeedbackTexts = (
  t: (key: string, params: Record<string, string>) => string,
  clause: keyof Omit<FeedbackTexts, 'governingLaw' | 'disputeResolution' | 'confidentiality'>,
  type: 'procurement' | 'sales',
) => {
  const feedbackTexts: Omit<
    FeedbackTexts,
    'governingLaw' | 'disputeResolution' | 'confidentiality'
  > = {
    // Different strings for sales and procurement
    paymentTerm: {
      sales: {
        feedbackTextExample: t(
          'The Customer shall pay all invoices in full within (X) days from the date of receipt. Payments shall be made in (currency) to the bank account specified by the Seller, without any deductions, set-offs, or counterclaims.',
          {
            _comment:
              'Example text for payment term clause in sales contracts, specifying payment deadlines and conditions.',
          },
        ),
        feedbackTextWhatToDo: t(
          'A payment term specifies when a party must pay the invoice for goods or services, establishing clear expectations for both parties.\n\nThe terms must be clear and enforceable to ensure timely payment and maintain healthy cash flow. Ensure the payment schedule, due dates, and any conditions for withholding payment align with your business preferences.',
          {
            _comment:
              'Explanation of payment term clauses in sales contracts, their purpose, and considerations for reviewing them.',
          },
        ),
      },
      procurement: {
        feedbackTextExample: t(
          'The Buyer shall pay all undisputed invoices in full within (X) days from the date of receipt, in (currency), to the bank account specified by the Seller. If any invoice contains amounts that the Buyer disputes in good faith—due to the Seller’s non-performance, breach, or failure to comply with this Agreement—the Buyer shall notify the Seller in writing within (Y) days of receipt, detailing the nature of the dispute. The Buyer may withhold or set off the disputed amounts until the issue is resolved, provided that payment of any undisputed amounts shall not be affected.',
          {
            _comment:
              'Example text for payment term clause in procurement contracts, specifying payment deadlines and dispute handling.',
          },
        ),
        feedbackTextWhatToDo: t(
          'Payment terms specify when a party must pay the invoice for goods or services, establishing clear expectations for both parties.\n\nReview the payment schedule, due dates, and any conditions for withholding payment to ensure they align with your business needs.',
          {
            _comment:
              'Explanation of payment term clauses in procurement contracts, their purpose, and considerations for reviewing them.',
          },
        ),
      },
    },
    exclusionOfConsequentialLoss: {
      sales: {
        feedbackTextExample: t(
          'To the maximum extent permitted by law, the Company shall not be liable for any incidental, consequential, punitive, exemplary, special, or indirect damages, including but not limited to loss of profit, revenue, savings, goodwill, business opportunities, data, or investments, nor for any costs related to capital, substitute goods or services, downtime, or third-party claims, arising out of or in connection with this Agreement, whether in contract, tort (including negligence), strict liability, or any other legal theory, even if the Company has been advised of the possibility of such damages.',
          {
            _comment:
              'Example text for exclusion of consequential damages clause in sales contracts, specifying excluded liabilities.',
          },
        ),
        feedbackTextWhatToDo: t(
          'This clause excludes liability for indirect and consequential damages, commonly included to protect both parties. Its purpose is to limit liability solely to direct damages, enhancing clarity and safeguards for everyone involved. \n\nReview it carefully to ensure it meets your business needs, such as protecting against uncontrolled liabilities while allowing for sufficient recovery in case of a breach.',
          {
            _comment:
              'Explanation of exclusion of consequential damages clauses in sales contracts, their purpose, and considerations for reviewing them.',
          },
        ),
      },
      procurement: {
        feedbackTextExample: t(
          'Except as expressly provided in this Agreement, neither party shall be liable to the other for any indirect, incidental, consequential, special, punitive, or exemplary damages arising out of or related to this Agreement, regardless of the legal theory under which such damages are claimed (including contract, tort—including negligence—or strict liability), even if such party has been advised of the possibility of such damages. This exclusion applies to all claims, including but not limited to profit losses, revenue, data, goodwill, business opportunities, and any other indirect or consequential losses.\n\nNotwithstanding the foregoing, nothing in this Agreement shall limit or exclude liability for:\n* Damages arising from a party’s gross negligence or willful misconduct;\n* Any liability that cannot be limited or excluded under applicable law.',
          {
            _comment:
              'Example text for exclusion of consequential damages clause in procurement contracts, specifying excluded liabilities and exceptions.',
          },
        ),
        feedbackTextWhatToDo: t(
          'This clause excludes liability for indirect and consequential damages, which is commonly included to protect both parties. Its purpose is to limit liability solely to direct damages, enhancing clarity and safeguards.\n\nReview it carefully to ensure it meets your business needs, including protection against potential gaps in recovery for significant losses in the event of a breach.',
          {
            _comment:
              'Explanation of exclusion of consequential damages clauses in procurement contracts, their purpose, and considerations for reviewing them.',
          },
        ),
      },
    },
    forceMajeure: {
      sales: {
        feedbackTextExample: t(
          'Neither Party shall be liable for any failure or delay in performing its obligations under this Agreement (excluding payment obligations) if such failure or delay is due to an event beyond its reasonable control, the consequences of which could not have been prevented or mitigated through reasonable measures. Such events include, but are not limited to, natural disasters, war, acts of terrorism, governmental actions, labor strikes, supply chain disruptions, or other unforeseen circumstances. The affected Party shall promptly notify the other Party in writing and take reasonable steps to resume performance as soon as practicable. If the force majeure event continues for more than (X) days, either Party may terminate the Agreement upon written notice.',
          {
            _comment:
              'Example text for force majeure clause in sales contracts, specifying events beyond reasonable control and termination conditions.',
          },
        ),
        feedbackTextWhatToDo: t(
          'Force majeure clauses relieve parties from liability when unforeseen events beyond their control—such as natural disasters or government actions—prevent them from fulfilling contractual obligations.\n\nThese clauses aim to balance risk in unpredictable situations. Review them carefully to ensure they cover your business needs, including relevant events, reasonable response times, and provisions that protect your interests in case of extended disruptions.',
          {
            _comment:
              'Explanation of force majeure clauses, their purpose, and considerations for reviewing them in sales contracts.',
          },
        ),
      },
      procurement: {
        feedbackTextExample: t(
          'Except for payment obligations, neither Party shall be liable for delays or failures in performance solely to the extent they are caused by a force majeure event. For this Agreement, a “force majeure event” is an occurrence beyond the reasonable control of the affected Party that could not be prevented or mitigated by reasonable diligence and is limited to natural disasters, acts of war or terrorism, and governmental actions that directly restrict performance. The Seller may not invoke force majeure for commercially foreseeable events such as supply chain disruptions or labor disputes. The affected Party must promptly notify the other in writing, detailing the event, its expected duration, and steps taken to mitigate its effects. If the event continues for more than (X) days, the unaffected Party may terminate this Agreement upon written notice.',
          {
            _comment:
              'Example text for force majeure clause in procurement contracts, specifying events beyond reasonable control and exceptions.',
          },
        ),
        feedbackTextWhatToDo: t(
          'Force majeure clauses relieve parties from liability when unforeseen events beyond their control—such as natural disasters or government actions—prevent them from fulfilling contractual obligations. \n\nWhile these clauses aim to balance risk in unpredictable situations, they should not be overly protective of the other party. Review them carefully to ensure they cover your business needs, including relevant events, reasonable response times, and how obligations and entitlement to compensation will resume once the disruption ends.',
          {
            _comment:
              'Explanation of force majeure clauses, their purpose, and considerations for reviewing them in procurement contracts.',
          },
        ),
      },
    },
    indemnities: {
      sales: {
        feedbackTextExample: t(
          'The Customer shall indemnify and hold the Seller harmless from and against all claims, losses, liabilities, damages, costs, and expenses (including reasonable legal fees) arising out of or related to (specific indemnified events, e.g., Customer’s misuse of the product, non-compliance with applicable laws, or third-party claims resulting from the Customer’s actions), except to the extent such losses are caused solely by the Seller’s gross negligence or willful misconduct.\n\nThe Customer shall not be entitled to an indemnity under this clause to the extent it fails to take reasonable steps to mitigate its losses. Any indemnity claims must be notified to the Seller in writing within (specified timeframe, e.g., 30 days) of the Customer becoming aware of the claim. The Seller reserves the right to control the defense and settlement of any indemnified claim.',
          {
            _comment:
              'Example text for indemnity clause in sales contracts, specifying indemnified events and claims-handling process.',
          },
        ),
        feedbackTextWhatToDo: t(
          'Indemnity clauses protect one party by requiring the other to compensate for losses, damages, or liabilities arising from specific situations, such as breaches or third-party claims. These clauses are vital for managing risk. Ensure the scope, limitations and caps, expirations, and claims-handling process are aligned with your business needs.',
          {
            _comment:
              'Explanation of indemnity clauses, their purpose, and considerations for reviewing them in sales contracts.',
          },
        ),
      },
      procurement: {
        feedbackTextExample: t(
          'Seller shall indemnify, defend, and hold harmless Buyer and its affiliates from any claims, losses, liabilities, damages, costs, or expenses (including reasonable attorneys’ fees) arising out of or relating to (i) any breach by Seller of its representations, warranties, or covenants under this Agreement, (ii) Seller’s gross negligence or willful misconduct, or (iii) any claim that Seller’s products or services infringe any third party’s intellectual property rights. Buyer shall promptly notify Seller in writing of any such claim, and Seller shall control the defense and settlement, provided that Seller may not settle in a manner that imposes liability on Buyer without its prior written consent. Seller’s indemnity obligations shall survive termination of this Agreement.\n\nWhere Buyer is obligated to indemnify Seller—for example, due to Buyer’s misuse of the products or non-compliance with applicable laws—such indemnity shall require Buyer to mitigate its losses and must be invoked by Seller with written notice within (X) days of its awareness of the claim.',
          {
            _comment:
              'Example text for indemnity clause in procurement contracts, specifying indemnified events and claims-handling process.',
          },
        ),
        feedbackTextWhatToDo: t(
          'Indemnity clauses protect one party by requiring the other to compensate for losses, damages, or liabilities arising from specific situations, such as breaches or third-party claims. These clauses are vital for managing risk. Ensure the scope, limitations and caps, expirations, and claims-handling process are aligned with your business needs.',
          {
            _comment:
              'Explanation of indemnity clauses, their purpose, and considerations for reviewing them in procurement contracts.',
          },
        ),
      },
    },
    limitationOfLiability: {
      sales: {
        feedbackTextExample: t(
          'The Seller’s total aggregate liability arising out of or in connection with this Agreement, whether in contract, tort (including negligence), or any other legal theory, shall not exceed (X)% of the total value of the relevant purchase order.\n\nThese limitations shall apply to the maximum extent permitted by law, regardless of the nature of the claim or cause of action.',
          {
            _comment:
              'Example text for limitation of liability clause in sales contracts, specifying liability caps.',
          },
        ),
        feedbackTextWhatToDo: t(
          'Limitation of liability clauses cap the amount or types of damages one party can recover from the other in case of a breach or legal claims. \n\nThese clauses are essential for controlling risk exposure. Ensure the limitation of liability aligns with your business needs, protecting against excessive liability.',
          {
            _comment:
              'Explanation of limitation of liability clauses, their purpose, and considerations for reviewing them in sales contracts.',
          },
        ),
      },
      procurement: {
        feedbackTextExample: t(
          'Notwithstanding any other provision in this Agreement, Seller’s total aggregate liability for direct damages arising out of or in connection with this Agreement shall not exceed (X)% of the purchase order value, except that this cap shall not apply to claims arising from Seller’s breach of express warranties or indemnification obligations, or from Seller’s gross negligence, willful misconduct, or fraud. Furthermore, Seller shall remain liable for any indirect, consequential, or punitive damages resulting from such breaches. If any portion of this limitation is held unenforceable, liability shall be determined in accordance with applicable law.',
          {
            _comment:
              'Example text for limitation of liability clause in procurement contracts, specifying liability caps and exceptions.',
          },
        ),
        feedbackTextWhatToDo: t(
          'Limitation of liability clauses cap the amount or types of damages one party can recover from the other in case of a breach or legal claims.\n\nThese clauses are essential for controlling risk exposure. Ensure the limitation of liability aligns with your business needs, protecting you against any excessive liability while allowing for reasonable recovery when necessary.',
          {
            _comment:
              'Explanation of limitation of liability clauses, their purpose, and considerations for reviewing them in procurement contracts.',
          },
        ),
      },
    },
    liquidatedDamages: {
      sales: {
        feedbackTextExample: t(
          'If the Seller fails to meet its obligations under this Agreement, the Seller’s liability shall be limited to the payment of liquidated damages in the amount of (X)% of the relevant purchase order value per (day/week) of delay, up to a maximum of (Y)% of the total purchase order value.\n\nThe Parties acknowledge that this amount is a genuine pre-estimate of the losses that may arise and is not a penalty. Liquidated damages shall be the sole and exclusive remedy for the specified breach, and the Customer waives any right to claim additional damages beyond the agreed liquidated amount.',
          {
            _comment:
              'Example text for liquidated damages clause in sales contracts, specifying pre-agreed compensation for breaches.',
          },
        ),
        feedbackTextWhatToDo: t(
          'Liquidated damages clauses specify a pre-agreed amount of compensation that one party must pay the other in case of specific breaches, such as delays or failure to meet performance standards.\n\nWhile they offer clarity, be restrictive when accepting liquidated damages, as the fixed amount may not always reflect the true extent of losses and could lead to overpayment. Ensure the liquidated damages are reasonable, reflect anticipated losses, and align with your business needs to protect your interests while maintaining fairness.',
          {
            _comment:
              'Explanation of liquidated damages clauses, their purpose, and considerations for reviewing them in sales contracts.',
          },
        ),
      },
      procurement: {
        feedbackTextExample: t(
          'If the Seller fails to timely perform its obligations under this Agreement, the Seller shall pay Buyer liquidated damages at a rate of (X)% of the relevant purchase order value for each (day/week) of delay, not to exceed (Y)% of the total purchase order value. The Parties acknowledge that this amount represents a genuine pre-estimate of Buyer’s losses from such delay and is not a penalty. Liquidated damages shall be Buyer’s sole remedy for any delay-related breach.',
          {
            _comment:
              'Example text for liquidated damages clause in procurement contracts, specifying pre-agreed compensation for breaches.',
          },
        ),
        feedbackTextWhatToDo: t(
          'Liquidated damages clauses specify a pre-agreed amount of compensation for specific breaches, such as delays or failure to meet performance standards. \n\nWhile these clauses provide clarity, the fixed amount may not always reflect the true extent of potential losses, which could leave your business under-compensated. Ensure the liquidated damages are reasonable, reflect anticipated impacts, and align with your business needs to protect your interests while maintaining fairness.',
          {
            _comment:
              'Explanation of liquidated damages clauses, their purpose, and considerations for reviewing them in procurement contracts.',
          },
        ),
      },
    },
  };

  return feedbackTexts[clause][type];
};
